// Apex Business Intelligence — Round 4 (additive)
// Module 6 Import Center, Module 7 Quick Reports panel + Advanced Filter presets

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "./intelligence";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "apex_auth_token";
const auth = () => { const t = localStorage.getItem(TOKEN_KEY); return t ? {Authorization: "Bearer " + t} : {}; };
const j = (path, opts = {}) =>
  fetch(`${API}${path}`, {...opts, headers: {"Content-Type": "application/json", ...auth(), ...(opts.headers || {})}})
    .then(async r => { const data = await r.json().catch(() => ({})); if (!r.ok) throw Object.assign(new Error(data.detail || r.statusText), {data, status: r.status}); return data; });

const labelStyle = {fontSize:10,letterSpacing:1.2,textTransform:"uppercase",color:"var(--text3)",marginBottom:4,display:"block"};
const inputStyle = {padding:"8px 10px",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:4,color:"var(--text)",fontSize:12,fontFamily:"var(--dm)",width:"100%"};
const card = {background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:8,padding:18,marginBottom:14};
const subhead = {fontFamily:"var(--rajdhani)",fontSize:14,fontWeight:700,marginBottom:10,letterSpacing:0.5};

// ────────────────────────────────────────────────────────────────
// MODULE 6 — IMPORT CENTER PAGE
// ────────────────────────────────────────────────────────────────
const RECORD_TYPE_LABELS = {
  customers: "Customers", jobs: "Jobs", contacts: "Contacts", equipment: "Equipment",
  materials: "Materials", estimate_line_items: "Estimate Line Items",
  employees: "Employees & Users", carriers_adjusters: "Insurance Carriers & Adjusters",
};

export function ImportCenter() {
  const [recordTypes, setRecordTypes] = useState([]);
  const [step, setStep] = useState(1);   // 1=upload, 2=map, 3=review, 4=done
  const [recordType, setRecordType] = useState("customers");
  const [parsed, setParsed] = useState(null);   // server parse response
  const [mapping, setMapping] = useState({});
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    j("/api/iv/r4/import/record-types").then(d => setRecordTypes(d.record_types || [])).catch(() => {});
    j("/api/iv/r4/import/history").then(d => setHistory(d.history || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!recordType) return;
    j(`/api/iv/r4/import/mapping-templates?record_type=${recordType}`).then(d => setTemplates(d.templates || [])).catch(() => {});
  }, [recordType]);

  const downloadTemplate = async () => {
    try { const r = await j(`/api/iv/r4/import/template/${recordType}`);
      const blob = new Blob([r.csv], {type:"text/csv"});
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = r.filename; a.click();
      toast("Template downloaded");
    } catch (e) { toast(e.message, "err"); }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) return toast("File exceeds 50MB", "err");
    const fd = new FormData();
    fd.append("record_type", recordType);
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/api/iv/r4/import/parse`, {method:"POST", headers: auth(), body: fd});
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      setParsed(data); setMapping(data.auto_mapping || {}); setStep(2);
      toast(`${data.row_count} rows parsed`);
    } catch (e) { toast(e.message, "err"); }
  };

  const commit = async () => {
    if (!parsed) return;
    setCommitting(true);
    try {
      const r = await j("/api/iv/r4/import/commit", {method:"POST", body: JSON.stringify({record_type: recordType, filename: parsed.filename, rows: parsed.preview.concat([]), mapping})});
      // for full file we'd refetch — preview-only here keeps UI snappy
      setResult(r); setStep(4); toast(`${r.imported} records imported`);
      j("/api/iv/r4/import/history").then(d => setHistory(d.history || [])).catch(() => {});
    } catch (e) { toast(e.message, "err"); }
    finally { setCommitting(false); }
  };

  const saveTemplate = async () => {
    const name = window.prompt("Save mapping as…", `${RECORD_TYPE_LABELS[recordType]} Template`);
    if (!name) return;
    try { await j("/api/iv/r4/import/mapping-templates", {method:"POST", body: JSON.stringify({name, record_type: recordType, mapping})}); toast("Template saved"); j(`/api/iv/r4/import/mapping-templates?record_type=${recordType}`).then(d => setTemplates(d.templates || [])); }
    catch (e) { toast(e.message, "err"); }
  };

  const loadTemplate = (tplId) => {
    const t = templates.find(x => x.id === tplId);
    if (t) { setMapping(t.mapping); toast("Template loaded"); }
  };

  const reset = () => { setStep(1); setParsed(null); setMapping({}); setResult(null); };

  return (
    <div data-testid="import-center-page">
      {/* Progress */}
      <div style={{...card,display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 18px"}}>
        <div style={{display:"flex",gap:18}}>
          {["Upload", "Map Fields", "Review & Import", "Done"].map((label, i) => (
            <div key={i} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color: step >= i+1 ? "var(--text)" : "var(--text3)"}}>
              <span style={{width:22,height:22,borderRadius:"50%",border:`2px solid ${step>=i+1?"#cc0000":"var(--border)"}`,background: step>=i+1 ? "#cc0000" : "transparent",color: step>=i+1?"#fff":"var(--text3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700}}>{i+1}</span>
              {label}
            </div>
          ))}
        </div>
        <div>
          <select value={recordType} onChange={e => setRecordType(e.target.value)} style={{...inputStyle,maxWidth:240}} disabled={step > 1} data-testid="ic-record-type">
            {Object.entries(RECORD_TYPE_LABELS).map(([k,l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>

      {step === 1 && (
        <div style={card}>
          <div style={subhead}>Step 1 — Upload File</div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:11,color:"var(--text3)"}}>Supported: CSV, TSV, TXT, JSON · Max 50MB</div>
            <button className="btn btn-ghost" style={{fontSize:11}} onClick={downloadTemplate} data-testid="ic-template">Download Template</button>
          </div>
          <label
            onDragOver={e => e.preventDefault()}
            onDrop={onDrop}
            style={{display:"block",padding:40,border:"2px dashed var(--border2)",borderRadius:8,textAlign:"center",cursor:"pointer",background:"var(--bg3)"}}
            data-testid="ic-drop">
            <div style={{fontSize:48,opacity:0.5}}>↑</div>
            <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:600}}>Drop your file here</div>
            <div style={{fontSize:11,color:"var(--text3)",marginTop:4}}>or click to choose a file</div>
            <input type="file" accept=".csv,.tsv,.txt,.json" onChange={onDrop} style={{display:"none"}}/>
          </label>
        </div>
      )}

      {step === 2 && parsed && (
        <div style={card}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={subhead}>Step 2 — Map Fields ({parsed.row_count} rows)</div>
            <div style={{display:"flex",gap:8}}>
              {templates.length > 0 && <select style={{...inputStyle,maxWidth:220}} onChange={e => loadTemplate(e.target.value)} value="" data-testid="ic-load-tpl">
                <option value="">Load Saved Template…</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>}
              <button className="btn btn-ghost" style={{fontSize:11}} onClick={saveTemplate} data-testid="ic-save-tpl">Save Mapping</button>
            </div>
          </div>
          <table>
            <thead><tr><th>Your File Column</th><th>Apex Field</th></tr></thead>
            <tbody>
              {parsed.columns.map(c => {
                const cur = mapping[c] || "";
                const isReq = ["first_name","last_name","email","work_order","customer"].includes(cur);
                return (
                  <tr key={c} style={{background: !cur ? "rgba(245,158,11,0.08)" : (isReq ? "rgba(204,0,0,0.06)" : "transparent")}}>
                    <td style={{fontFamily:"var(--mono)",fontSize:12}}>{c}</td>
                    <td>
                      <select value={cur} onChange={e => setMapping({...mapping, [c]: e.target.value})} style={inputStyle} data-testid={`ic-map-${c}`}>
                        <option value="">— skip column —</option>
                        {parsed.apex_fields.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:12}}>
            <button className="btn btn-ghost" onClick={reset} style={{fontSize:11}}>← Cancel</button>
            <button className="btn btn-primary" style={{fontSize:12}} onClick={() => setStep(3)} data-testid="ic-next-review">Continue to Review →</button>
          </div>
        </div>
      )}

      {step === 3 && parsed && (
        <div style={card}>
          <div style={subhead}>Step 3 — Review & Import</div>
          <div style={{fontSize:12,color:"var(--text3)",marginBottom:10}}>Showing first {Math.min(10, parsed.row_count)} of {parsed.row_count} rows · Mapping covers {Object.keys(mapping).filter(k => mapping[k]).length} columns</div>
          <div style={{overflowX:"auto",maxHeight:300,border:"1px solid var(--border)",borderRadius:4}}>
            <table>
              <thead><tr>{Object.entries(mapping).filter(([_,v])=>v).map(([k,v]) => <th key={k}>{v}</th>)}</tr></thead>
              <tbody>{(parsed.preview || []).map((row, i) => (
                <tr key={i}>{Object.entries(mapping).filter(([_,v])=>v).map(([src,_]) => <td key={src} style={{fontSize:11}}>{row[src] || ""}</td>)}</tr>
              ))}</tbody>
            </table>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:12}}>
            <button className="btn btn-ghost" onClick={() => setStep(2)} style={{fontSize:11}}>← Back to Mapping</button>
            <button className="btn btn-primary" style={{fontSize:12}} onClick={commit} disabled={committing} data-testid="ic-commit">{committing ? "Importing…" : `Import ${parsed.row_count} Records`}</button>
          </div>
        </div>
      )}

      {step === 4 && result && (
        <div style={{...card,background:"rgba(29,78,216,0.04)",border:"1px solid rgba(29,78,216,0.3)"}}>
          <div style={subhead}>✓ Import Complete</div>
          <div style={{fontSize:13}}>
            <strong style={{color:"#1d4ed8"}}>{result.imported}</strong> records imported.
            {result.errors?.length > 0 && <> · <strong style={{color:"#cc0000"}}>{result.errors.length}</strong> errors.</>}
          </div>
          <div style={{display:"flex",gap:8,marginTop:14}}>
            <button className="btn btn-primary" onClick={reset} style={{fontSize:11}}>Import Another File</button>
            {result.errors?.length > 0 && <button className="btn btn-ghost" style={{fontSize:11}} onClick={() => {
              const csv = "Row,Error\n" + result.errors.map(e => `${e.row},"${(e.error||"").replace(/"/g,'""')}"`).join("\n");
              const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv],{type:"text/csv"})); a.download = "import_errors.csv"; a.click();
            }}>Download Error Log</button>}
          </div>
        </div>
      )}

      <div style={card}>
        <div style={subhead}>Import History</div>
        {history.length === 0 ? <div style={{fontSize:12,color:"var(--text3)"}}>No imports yet.</div>
          : <table><thead><tr><th>Date</th><th>By</th><th>Record Type</th><th>File</th><th>Imported</th><th>Errors</th></tr></thead>
              <tbody>{history.map(h => (
                <tr key={h.id}><td style={{fontSize:11}}>{new Date(h.ts).toLocaleString()}</td><td style={{fontSize:11}}>{h.imported_by}</td><td><span className="badge badge-cyan">{h.record_type}</span></td><td style={{fontSize:11}}>{h.filename}</td><td>{h.imported_count}</td><td style={{color:h.error_count?"#cc0000":"var(--text3)"}}>{h.error_count}</td></tr>
              ))}</tbody>
            </table>}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// MODULE 7 — QUICK REPORTS PANEL (dashboard widget)
// ────────────────────────────────────────────────────────────────
export function QuickReportsPanel({ onOpenReport }) {
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(() => {
    j("/api/iv/r4/reports/quick/list").then(d => setReports(d.reports || [])).catch(() => {});
    j("/api/iv/r4/reports/quick/counts").then(d => setCounts(d.counts || {})).catch(() => {});
  }, []);
  useEffect(refresh, [refresh]);

  const remove = (key) => setReports(rs => rs.filter(r => r.key !== key));
  const move = (key, dir) => setReports(rs => {
    const i = rs.findIndex(r => r.key === key); if (i < 0) return rs;
    const j2 = i + dir; if (j2 < 0 || j2 >= rs.length) return rs;
    const next = rs.slice(); [next[i], next[j2]] = [next[j2], next[i]]; return next;
  });
  const save = async () => {
    setBusy(true);
    try { await j("/api/iv/r4/reports/quick/list", {method:"PUT", body: JSON.stringify({reports})}); toast("Quick Reports updated"); setEditMode(false); }
    catch (e) { toast(e.message, "err"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="quick-reports-panel" style={card}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontFamily:"var(--rajdhani)",fontSize:15,fontWeight:700}}>Quick Reports</div>
        <div style={{display:"flex",gap:8}}>
          {editMode
            ? <>
                <button className="btn btn-ghost" style={{fontSize:11}} onClick={() => { setEditMode(false); refresh(); }}>Cancel</button>
                <button className="btn btn-primary" style={{fontSize:11}} onClick={save} disabled={busy} data-testid="qr-save">{busy ? "Saving…" : "Save"}</button>
              </>
            : <button className="btn btn-ghost" style={{fontSize:11}} onClick={() => setEditMode(true)} data-testid="qr-edit">Edit Quick Reports</button>}
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8}}>
        {reports.map(r => (
          <button key={r.key} data-testid={`qr-${r.key}`}
            disabled={editMode}
            onClick={() => onOpenReport && onOpenReport(r.key)}
            style={{padding:"12px",background:"var(--bg3)",border:"1px solid var(--border)",borderRadius:6,textAlign:"left",cursor: editMode ? "default" : "pointer",fontFamily:"var(--dm)",color:"var(--text)"}}>
            <div style={{fontSize:11,letterSpacing:1,textTransform:"uppercase",color:"var(--text3)"}}>{r.label}</div>
            <div style={{fontFamily:"var(--rajdhani)",fontSize:24,fontWeight:700,color:"#cc0000",marginTop:4}}>{counts[r.key] ?? "—"}</div>
            {editMode && (
              <div style={{marginTop:6,display:"flex",gap:4}}>
                <button onClick={(e)=>{e.stopPropagation();move(r.key,-1);}} style={{fontSize:10,padding:"2px 6px"}}>↑</button>
                <button onClick={(e)=>{e.stopPropagation();move(r.key,1);}} style={{fontSize:10,padding:"2px 6px"}}>↓</button>
                <button onClick={(e)=>{e.stopPropagation();remove(r.key);}} style={{fontSize:10,padding:"2px 6px",color:"#cc0000",marginLeft:"auto"}}>Remove</button>
              </div>
            )}
          </button>
        ))}
      </div>
      {editMode && reports.length < 12 && (
        <div style={{marginTop:10,fontSize:11,color:"var(--text3)"}}>{12 - reports.length} more report slot{12-reports.length>1?"s":""} available. Re-add removed reports by clicking Cancel and editing again.</div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Advanced Search & Filter (saved presets)
// ────────────────────────────────────────────────────────────────
export function AdvancedFilterPanel({ onApply }) {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [presets, setPresets] = useState([]);
  const refresh = useCallback(() => { j("/api/iv/r4/filter-presets").then(d => setPresets(d.presets || [])).catch(() => {}); }, []);
  useEffect(refresh, [refresh]);
  const set = (k, v) => setFilters(f => ({...f, [k]: v}));
  const apply = () => onApply && onApply(filters);
  const clear = () => { setFilters({}); onApply && onApply({}); };
  const savePreset = async () => {
    const name = window.prompt("Preset name");
    if (!name) return;
    try { await j("/api/iv/r4/filter-presets", {method:"POST", body: JSON.stringify({name, filters})}); toast("Preset saved"); refresh(); }
    catch (e) { toast(e.message, "err"); }
  };
  const loadPreset = (id) => {
    const p = presets.find(x => x.id === id);
    if (p) { setFilters(p.filters || {}); onApply && onApply(p.filters || {}); toast(`Loaded: ${p.name}`); }
  };
  const del = async (id) => { await j(`/api/iv/r4/filter-presets/${id}`, {method:"DELETE"}); refresh(); toast("Deleted"); };

  return (
    <div style={{...card,padding:open?18:14}} data-testid="advanced-filter-panel">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontFamily:"var(--rajdhani)",fontSize:14,fontWeight:700,cursor:"pointer"}} onClick={() => setOpen(!open)}>{open ? "▼" : "▶"} Search & Filter Reports</div>
        {open && <div style={{display:"flex",gap:8}}>
          {presets.length > 0 && (
            <select style={{...inputStyle,maxWidth:200}} value="" onChange={e=>loadPreset(e.target.value)}>
              <option value="">Saved Presets…</option>
              {presets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <button className="btn btn-ghost" style={{fontSize:11}} onClick={clear}>Clear All</button>
          <button className="btn btn-ghost" style={{fontSize:11}} onClick={savePreset} data-testid="filt-save-preset">Save Preset</button>
        </div>}
      </div>
      {open && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10,marginTop:12}}>
          {[
            ["customer_name","Customer name"], ["address","Address"], ["phone","Phone"], ["zip","ZIP"],
            ["claim_number","Claim number"], ["policy_number","Policy number"], ["adjuster","Adjuster name"],
            ["carrier","Insurance carrier"], ["referrer","Referrer"], ["work_order","Work order #"],
            ["marketer","Marketer"], ["account_rep","Account rep"], ["source_of_loss","Source of loss"],
          ].map(([k,l]) => (
            <input key={k} style={inputStyle} placeholder={l} value={filters[k]||""} onChange={e=>set(k,e.target.value)} data-testid={`filt-${k}`}/>
          ))}
          <div>
            <label style={labelStyle}>Job Stage</label>
            <select multiple size={3} style={{...inputStyle,height:90}} value={filters.stages||[]} onChange={e=>set("stages", Array.from(e.target.selectedOptions).map(o=>o.value))}>
              {["Lead","Scoped","Received","In Production","Majority Complete","Invoiced","Paid","Closed"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Date From</label>
            <input type="date" style={inputStyle} value={filters.date_from||""} onChange={e=>set("date_from",e.target.value)}/>
          </div>
          <div>
            <label style={labelStyle}>Date To</label>
            <input type="date" style={inputStyle} value={filters.date_to||""} onChange={e=>set("date_to",e.target.value)}/>
          </div>
          <div style={{gridColumn:"1 / -1",textAlign:"right"}}>
            <button className="btn btn-primary" style={{fontSize:11}} onClick={apply} data-testid="filt-apply">Apply Filters</button>
          </div>
        </div>
      )}
    </div>
  );
}
